import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
// Bulk import of markdown / text / zip files with an optional metadata-review
// step. File parsing stays on the client so the worker stays cheap. JSZip is
// loaded lazily the first time the user picks a zip.
import { useEffect, useRef, useState } from "react";
import { api, ApiClientError } from "@/lib/api";
import { usePreferences } from "@/lib/preferences";
import { log } from "@/lib/logger";
import { CATEGORY_EMOJI, READMECATEGORIES } from "@shared/types";
const TEXT_EXTENSIONS = /\.(md|markdown|txt)$/i;
function titleFromFilename(filename) {
    const base = filename.replace(/^.*[\\/]/, "").replace(/\.(md|markdown|txt)$/i, "");
    return base.replace(/[-_]+/g, " ").trim() || "Untitled";
}
function folderToCategory(folder) {
    if (!folder)
        return null;
    const normalized = folder.toLowerCase().replace(/[-_]+/g, " ").trim().replace(/s$/, "");
    return READMECATEGORIES.find((c) => c === normalized) ?? null;
}
function buildReviewRows(files) {
    return files.map((file, i) => {
        const category = folderToCategory(file.folder);
        const title = titleFromFilename(file.filename);
        const emoji = category ? (CATEGORY_EMOJI[category] ?? "") : "";
        const tags = !category && file.folder
            ? file.folder
                .replace(/[^A-Za-z0-9_\-]/g, "-")
                .replace(/^-+|-+$/g, "")
                .slice(0, 50)
            : "";
        return { id: `row-${i}`, filepath: file.filename, file, title, emoji, category, tags, included: true };
    });
}
export function ImportModal({ open, onClose, onImported, initialFiles, }) {
    const { prefs, setPref } = usePreferences();
    const [step, setStep] = useState("pick");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(null);
    const [rows, setRows] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
    const [suggestProgress, setSuggestProgress] = useState(null);
    const fileInputRef = useRef(null);
    const dragCounterRef = useRef(0);
    // Auto-process dropped files from the main page when the modal opens
    useEffect(() => {
        if (!open || !initialFiles || initialFiles.length === 0)
            return;
        const dt = new DataTransfer();
        for (const f of initialFiles)
            dt.items.add(f);
        void handleFiles(dt.files);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    if (!open)
        return null;
    function resetAndClose() {
        setStep("pick");
        setError(null);
        setRows([]);
        setIsDragOver(false);
        dragCounterRef.current = 0;
        if (fileInputRef.current)
            fileInputRef.current.value = "";
        onClose();
    }
    async function handleFiles(fileList) {
        if (!fileList || fileList.length === 0)
            return;
        setError(null);
        setBusy(true);
        try {
            const files = [];
            const skipped = [];
            const list = Array.from(fileList);
            setProgress({ done: 0, total: list.length });
            for (const file of list) {
                if (/\.zip$/i.test(file.name)) {
                    files.push(...(await readZip(file)));
                }
                else if (TEXT_EXTENSIONS.test(file.name)) {
                    files.push({ filename: file.name, content: await file.text() });
                }
                else {
                    skipped.push(file.name);
                }
                setProgress((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
            }
            if (files.length === 0) {
                throw new Error(skipped.length > 0
                    ? `Nothing to import. Only .md, .markdown, .txt and .zip files are accepted (skipped ${skipped.length}).`
                    : "No files selected.");
            }
            if (prefs.importAutoApply) {
                await submitImport(files);
            }
            else {
                const reviewRows = buildReviewRows(files);
                setRows(reviewRows);
                setStep("review");
                // Auto-run AI suggest for all files; pass reviewRows directly so we
                // don't race against the setRows() state update landing.
                void handleSuggestAll(reviewRows);
            }
        }
        catch (err) {
            log.error("readme.import", { err: String(err) });
            setError(err instanceof Error ? err.message : "Import failed.");
        }
        finally {
            setBusy(false);
            setProgress(null);
            if (fileInputRef.current)
                fileInputRef.current.value = "";
        }
    }
    async function submitImport(files) {
        const res = await api.importReadmes({ files, folderAsCategory: prefs.importFolderAsCategory });
        onImported(res.imported);
        resetAndClose();
    }
    async function handleReviewImport() {
        const included = rows.filter((r) => r.included);
        if (included.length === 0)
            return;
        setError(null);
        setBusy(true);
        try {
            const files = included.map((row) => ({
                ...row.file,
                title: row.title.trim() || titleFromFilename(row.file.filename),
                icon: row.emoji.trim() || null,
                category: row.category,
                tags: row.tags
                    .split(",")
                    .map((t) => t.trim())
                    .filter(Boolean),
            }));
            await submitImport(files);
        }
        catch (err) {
            log.error("readme.import", { err: String(err) });
            setError(err instanceof ApiClientError ? err.message : err instanceof Error ? err.message : "Import failed.");
        }
        finally {
            setBusy(false);
        }
    }
    // targetRows lets callers pass the row list directly (e.g. right after
    // setRows() before the next render) instead of reading stale state.
    async function handleSuggestAll(targetRows) {
        if (suggesting)
            return;
        const targets = (targetRows ?? rows).filter((r) => r.included);
        if (targets.length === 0)
            return;
        setSuggesting(true);
        setSuggestProgress({ done: 0, total: targets.length });
        const knownCategories = [...READMECATEGORIES, ...Object.keys(prefs.customCategories ?? {})];
        for (const row of targets) {
            try {
                const res = await api.suggestMeta({
                    content: row.file.content.slice(0, 4000),
                    knownCategories,
                });
                setRows((prev) => prev.map((r) => r.id !== row.id
                    ? r
                    : {
                        ...r,
                        title: res.title || r.title,
                        emoji: res.icon || r.emoji,
                        category: res.category ?? r.category,
                        tags: res.tags.length > 0 ? res.tags.join(", ") : r.tags,
                    }));
            }
            catch (err) {
                log.warn("readme.suggest-meta", { file: row.filepath, err: String(err) });
            }
            setSuggestProgress((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
        }
        setSuggesting(false);
        setSuggestProgress(null);
    }
    function updateRow(id, key, value) {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
    }
    // Drag-and-drop handlers for the pick step.
    // We track a counter (dragCounterRef) because dragenter/dragleave fire on
    // child elements too — without counting, the "drag over" state flickers.
    function onDragEnter(e) {
        e.preventDefault();
        dragCounterRef.current += 1;
        setIsDragOver(true);
    }
    function onDragLeave(e) {
        e.preventDefault();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current <= 0) {
            dragCounterRef.current = 0;
            setIsDragOver(false);
        }
    }
    function onDragOver(e) {
        e.preventDefault();
    }
    function onDrop(e) {
        e.preventDefault();
        dragCounterRef.current = 0;
        setIsDragOver(false);
        // Prefer DataTransferItem.getAsFile() — more reliable on Mac than
        // dataTransfer.files which can be empty in certain Safari/Chrome builds.
        if (e.dataTransfer.items?.length > 0) {
            const dt = new DataTransfer();
            for (const item of Array.from(e.dataTransfer.items)) {
                if (item.kind === "file") {
                    const f = item.getAsFile();
                    if (f)
                        dt.items.add(f);
                }
            }
            if (dt.files.length > 0) {
                void handleFiles(dt.files);
                return;
            }
        }
        if (e.dataTransfer.files.length > 0)
            void handleFiles(e.dataTransfer.files);
    }
    const includedCount = rows.filter((r) => r.included).length;
    // ── review step ─────────────────────────────────────────────────────────────
    if (step === "review") {
        return (_jsx("div", { className: "fixed inset-0 z-40 flex items-end justify-center bg-slate-900/50 p-3 sm:items-center", onClick: resetAndClose, children: _jsxs("div", { className: "card w-full max-w-4xl p-4 sm:p-6", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "mb-3 flex items-center justify-between", children: [_jsxs("h2", { className: "text-lg font-semibold", children: ["Review import (", rows.length, " ", rows.length === 1 ? "file" : "files", ")"] }), _jsx("button", { type: "button", "aria-label": "Close", className: "rounded p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800", onClick: resetAndClose, children: "\u2715" })] }), _jsxs("div", { className: "mb-3 flex items-center justify-between gap-2", children: [_jsxs("p", { className: "text-sm text-slate-600 dark:text-slate-300", children: ["Review and adjust metadata before importing. Uncheck ", _jsx("strong", { children: "Include" }), " to skip a file."] }), _jsx("button", { className: "btn-secondary shrink-0 text-xs", onClick: () => void handleSuggestAll(), disabled: busy || suggesting || includedCount === 0, title: "Auto-fill title, category, tags and emoji with AI for all included files", children: suggesting
                                    ? suggestProgress
                                        ? `Suggesting… ${suggestProgress.done}/${suggestProgress.total}`
                                        : "Suggesting…"
                                    : "✦ Suggest all" })] }), _jsx("div", { className: "max-h-[50vh] overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700", children: _jsxs("table", { className: "w-full text-sm", children: [_jsx("thead", { className: "sticky top-0 bg-slate-50 dark:bg-slate-800", children: _jsxs("tr", { children: [_jsx("th", { className: "px-3 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-300", children: "File" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-300", children: "Title" }), _jsx("th", { className: "px-2 py-2 text-center text-xs font-semibold text-slate-600 dark:text-slate-300", children: "Emoji" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-300", children: "Category" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-300", children: "Tags" }), _jsx("th", { className: "px-3 py-2 text-center text-xs font-semibold text-slate-600 dark:text-slate-300", children: "Include" })] }) }), _jsx("tbody", { className: "divide-y divide-slate-100 dark:divide-slate-800", children: rows.map((row) => (_jsxs("tr", { className: row.included ? "" : "opacity-40", children: [_jsx("td", { className: "max-w-[130px] truncate px-3 py-2 text-xs text-slate-500 dark:text-slate-400", title: row.filepath, children: row.filepath }), _jsx("td", { className: "px-2 py-1.5", children: _jsx("input", { className: "input w-full min-w-[100px] py-1 text-xs", value: row.title, disabled: !row.included || busy || suggesting, onChange: (e) => updateRow(row.id, "title", e.target.value) }) }), _jsx("td", { className: "px-2 py-1.5", children: _jsx("input", { className: "input w-10 py-1 text-center text-base", value: row.emoji, disabled: !row.included || busy || suggesting, maxLength: 2, title: "Optional emoji (one character)", onChange: (e) => updateRow(row.id, "emoji", e.target.value) }) }), _jsx("td", { className: "px-2 py-1.5", children: _jsxs("select", { className: "input w-full py-1 text-xs", value: row.category ?? "", disabled: !row.included || busy || suggesting, onChange: (e) => {
                                                        const v = e.target.value;
                                                        updateRow(row.id, "category", v === "" ? null : v);
                                                    }, children: [_jsx("option", { value: "", children: "None" }), READMECATEGORIES.map((c) => (_jsx("option", { value: c, children: c }, c)))] }) }), _jsx("td", { className: "px-2 py-1.5", children: _jsx("input", { className: "input w-full min-w-[80px] py-1 text-xs", value: row.tags, disabled: !row.included || busy || suggesting, placeholder: "tag1, tag2", onChange: (e) => updateRow(row.id, "tags", e.target.value) }) }), _jsx("td", { className: "px-3 py-2 text-center", children: _jsx("input", { type: "checkbox", checked: row.included, disabled: busy || suggesting, onChange: (e) => updateRow(row.id, "included", e.target.checked) }) })] }, row.id))) })] }) }), error && (_jsx("div", { className: "mt-3 rounded-md bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", children: error })), _jsxs("div", { className: "mt-4 flex justify-between gap-2", children: [_jsx("button", { className: "btn-secondary", onClick: () => setStep("pick"), disabled: busy || suggesting, children: "Back" }), _jsx("button", { className: "btn-primary", onClick: handleReviewImport, disabled: busy || suggesting || includedCount === 0, children: busy ? "Importing…" : `Import ${includedCount} ${includedCount === 1 ? "file" : "files"}` })] })] }) }));
    }
    // ── pick step ────────────────────────────────────────────────────────────────
    return (_jsx("div", { className: "fixed inset-0 z-40 flex items-end justify-center bg-slate-900/50 p-3 sm:items-center", onClick: resetAndClose, children: _jsxs("div", { className: "card w-full max-w-lg p-4 sm:p-6", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "mb-3 flex items-center justify-between", children: [_jsx("h2", { className: "text-lg font-semibold", children: "Import READMEs" }), _jsx("button", { type: "button", "aria-label": "Close", className: "rounded p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800", onClick: resetAndClose, children: "\u2715" })] }), _jsxs("p", { className: "mb-3 text-sm text-slate-600 dark:text-slate-300", children: ["Drop in ", _jsx("code", { children: ".md" }), ", ", _jsx("code", { children: ".markdown" }), ", ", _jsx("code", { children: ".txt" }), ", or", " ", _jsx("code", { children: ".zip" }), " files. Inside a zip, every text file becomes a README \u2014 top-level folders matching a known category get assigned to that category, others become tags."] }), _jsxs("div", { className: "mb-3 flex flex-col gap-2 text-sm", children: [_jsxs("label", { className: "flex items-center gap-2", children: [_jsx("input", { type: "checkbox", checked: prefs.importAutoApply, disabled: busy, onChange: (e) => setPref("importAutoApply", e.target.checked) }), "Apply suggestions automatically", !prefs.importAutoApply && (_jsx("span", { className: "text-xs text-slate-500 dark:text-slate-400", children: "\u2014 you'll review metadata before importing" }))] }), _jsxs("label", { className: "flex items-center gap-2", children: [_jsx("input", { type: "checkbox", checked: prefs.importFolderAsCategory, disabled: busy, onChange: (e) => setPref("importFolderAsCategory", e.target.checked) }), "Use top-level folders as categories when possible"] })] }), _jsxs("div", { className: `flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors ${isDragOver
                        ? "border-sky-500 bg-sky-50 dark:border-sky-400 dark:bg-sky-950/20"
                        : "border-slate-300 dark:border-slate-600"}`, onDragEnter: onDragEnter, onDragLeave: onDragLeave, onDragOver: onDragOver, onDrop: onDrop, children: [_jsx("svg", { width: "36", height: "36", viewBox: "0 0 24 24", fill: "none", className: "text-slate-400", "aria-hidden": true, children: _jsx("path", { d: "M9 17H7a5 5 0 010-10h.5M15 17h2a5 5 0 000-10h-.5M12 12V3m0 0l-3 3m3-3l3 3", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }), _jsx("span", { className: "text-sm text-slate-500", children: isDragOver ? "Drop to import" : "Drag & drop files here, or" }), !isDragOver && (_jsx("button", { className: "rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-60", onClick: () => fileInputRef.current?.click(), disabled: busy, children: "Choose files" })), _jsx("input", { ref: fileInputRef, type: "file", multiple: true, accept: ".md,.markdown,.txt,.zip,text/markdown,text/plain,application/zip", disabled: busy, onChange: (e) => handleFiles(e.target.files), className: "hidden" })] }), progress && (_jsxs("p", { className: "mt-3 text-sm text-slate-600 dark:text-slate-300", children: ["Reading files\u2026 ", progress.done, "/", progress.total] })), error && (_jsx("div", { className: "mt-3 rounded-md bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", children: error })), _jsx("div", { className: "mt-4 flex justify-end", children: _jsx("button", { className: "btn-secondary", onClick: resetAndClose, disabled: busy, children: "Close" }) })] }) }));
}
// ── zip reader ────────────────────────────────────────────────────────────────
async function readZip(file) {
    const { default: JSZip } = await import("jszip");
    const zip = await JSZip.loadAsync(file);
    const out = [];
    const entries = Object.values(zip.files).filter((e) => {
        if (e.dir)
            return false;
        if (e.name.includes("__MACOSX/") || e.name.endsWith(".DS_Store"))
            return false;
        return TEXT_EXTENSIONS.test(e.name);
    });
    // Strip a transparent single-folder wrapper (e.g. GitHub archive exports).
    const rootFiles = entries.filter((e) => !e.name.includes("/"));
    const topFolders = new Set(entries.filter((e) => e.name.includes("/")).map((e) => e.name.split("/")[0]));
    const stripPrefix = rootFiles.length === 0 && topFolders.size === 1 ? [...topFolders][0] + "/" : null;
    // After the optional wrapper strip, find the common folder root so that a
    // zip like "x/A/f.md, x/B/f.md" correctly derives "A" and "B" as folders.
    const effectiveNames = entries.map((e) => stripPrefix && e.name.startsWith(stripPrefix) ? e.name.slice(stripPrefix.length) : e.name);
    const commonRoot = findCommonFolderRoot(effectiveNames);
    for (const entry of entries) {
        const content = await entry.async("string");
        let name = stripPrefix && entry.name.startsWith(stripPrefix)
            ? entry.name.slice(stripPrefix.length)
            : entry.name;
        if (commonRoot && name.startsWith(commonRoot))
            name = name.slice(commonRoot.length);
        const parts = name.split("/");
        const folder = parts.length > 1 ? parts[0] : null;
        out.push({ filename: name, content, folder });
    }
    return out;
}
// Returns the common folder prefix shared by all paths (e.g. "x/" for
// ["x/A/f.md","x/B/f.md"]).  Returns "" when any file is at the root level.
function findCommonFolderRoot(paths) {
    if (paths.length === 0)
        return "";
    if (paths.some((p) => !p.includes("/")))
        return ""; // file at root → no strip
    const folderSegs = paths.map((p) => p.split("/").slice(0, -1));
    const shortest = folderSegs.reduce((a, b) => (a.length <= b.length ? a : b));
    const common = [];
    for (let i = 0; i < shortest.length; i++) {
        if (folderSegs.every((s) => s[i] === shortest[i]))
            common.push(shortest[i]);
        else
            break;
    }
    return common.length > 0 ? common.join("/") + "/" : "";
}
